import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Scale, ShieldAlert, Cpu, Crown, Workflow, DollarSign, Megaphone,
  ArrowLeft, Send, Loader2, User as UserIcon, GraduationCap,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const ICON_MAP = { Scale, ShieldAlert, Cpu, Crown, Workflow, DollarSign, Megaphone };

export default function AIEmployeeChat() {
  const { personaId } = useParams();
  const [personas, setPersonas] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get('/ai/employees/').then(({ data }) => setPersonas(data));
  }, []);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 99999, behavior: 'smooth' });
  }, [messages, sending]);

  const persona = personas.find((p) => p.id === personaId);
  const Icon = persona ? (ICON_MAP[persona.icon] || UserIcon) : UserIcon;

  const send = async () => {
    if (!input.trim() || sending) return;
    const user_msg = input.trim();
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: user_msg }]);
    setSending(true);
    try {
      const { data } = await api.post('/ai/employees/chat', {
        persona: personaId,
        message: user_msg,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI provider failed.');
      setMessages((m) => [...m, {
        role: 'assistant',
        content: '— I was unable to respond. Please try again.',
        error: true,
      }]);
    } finally { setSending(false); }
  };

  if (!persona) {
    return <div className="ve-section text-sm text-slate-500">Loading employee profile…</div>;
  }

  return (
    <div className="ve-section">
      <Link to="/dashboard/ai-employees" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#5E1F8B] font-bold uppercase tracking-[0.18em]" data-testid="ai-chat-back">
        <ArrowLeft size={12}/> Back to AI Employees
      </Link>

      {/* Header */}
      <div className="mt-4 ve-card p-5 flex items-start gap-4">
        <div className="h-16 w-16 rounded-full border-2 border-[#8B3FC5] bg-black flex items-center justify-center shrink-0">
          <Icon size={28} className="text-[#8B3FC5]"/>
        </div>
        <div className="flex-1">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="display text-3xl font-bold leading-tight">{persona.english_name}</h1>
            <span className="text-2xl text-[#8B3FC5] font-bold leading-tight" dir="rtl">{persona.arabic_name}</span>
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
            {persona.english_role}
          </div>
          <p className="mt-2 text-sm text-slate-600">{persona.tagline}</p>
        </div>
        <Link
          to={`/dashboard/ai-employees/${personaId}/lessons`}
          data-testid="ai-chat-view-lessons"
          className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#8B3FC5]/40 text-[#5E1F8B] dark:text-[#8B3FC5] text-[10px] uppercase tracking-[0.22em] font-bold hover:bg-[#FFF8E5] dark:hover:bg-[#3a2f0a] transition shrink-0"
        >
          <GraduationCap size={12} /> Lesson history
        </Link>
      </div>

      {/* Conversation */}
      <div
        ref={scrollRef}
        className="mt-4 ve-card p-5 h-[480px] overflow-y-auto space-y-4"
        data-testid="ai-chat-thread"
      >
        {messages.length === 0 && (
          <div className="text-center text-sm text-slate-500 pt-16">
            <p className="mb-2">Start by asking {persona.english_name} anything from their domain.</p>
            <p className="text-xs">Example: <i>&ldquo;Draft a 3-clause MOU template for a Qatar-UAE joint venture.&rdquo;</i></p>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            data-testid={`ai-chat-msg-${m.role}-${i}`}
          >
            {m.role === 'assistant' && (
              <div className="h-9 w-9 rounded-full border border-[#8B3FC5]/40 bg-black flex items-center justify-center shrink-0">
                <Icon size={16} className="text-[#8B3FC5]"/>
              </div>
            )}
            <div className={`max-w-[80%] rounded-lg px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
              m.role === 'user'
                ? 'bg-[#0A0A0A] text-white'
                : m.error
                  ? 'bg-rose-50 border border-rose-200 text-rose-800'
                  : 'bg-[#FFF8E5] border border-[#8B3FC5]/30 text-slate-900'
            }`}>
              {m.content}
            </div>
            {m.role === 'user' && (
              <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
                <UserIcon size={16} className="text-slate-600"/>
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex gap-3 justify-start" data-testid="ai-chat-thinking">
            <div className="h-9 w-9 rounded-full border border-[#8B3FC5]/40 bg-black flex items-center justify-center shrink-0">
              <Icon size={16} className="text-[#8B3FC5]"/>
            </div>
            <div className="bg-[#FFF8E5] border border-[#8B3FC5]/30 rounded-lg px-4 py-3 text-sm text-slate-500 inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin"/> {persona.english_name} is thinking…
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="mt-4 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={`Ask ${persona.english_name}…`}
          disabled={sending}
          data-testid="ai-chat-input"
          className="flex-1 rounded-md border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-[#8B3FC5]"
        />
        <button
          onClick={send}
          disabled={sending || !input.trim()}
          data-testid="ai-chat-send"
          className="ve-btn-primary text-sm disabled:opacity-50"
        >
          {sending ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}
          Send
        </button>
      </div>
    </div>
  );
}
